import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { message } from '@myua/kit';
import { Dispatch } from 'redux';
import { api } from '../../../general/apiScenarios';
import { detectFonts } from '../../../general/fontLicense/fontDetection';
import { requestLicenseConfirmation } from '../../../general/fontLicense/LicenseConfirmationService';
import { bindLicenseIdsToFiles } from '../../../general/fontLicense/licenseRegistry';
import type { RootStore } from '../../reducers';
import { getAssetLabLogger } from '../app/logs';
import { getSettings } from '../app/selectors';
import { getSelectedId } from '../category/selectors';
import { getSelectedIds as getSelectedProjectIds } from '../project/selectors';
import * as finderActions from '../finder/actions';
import * as actions from './actions';
import { clearRegistry, registerFile } from './fileRegistry';
import { Job, JobStatus, makeJob } from './model';
import { getEnabled, getUploadToProjectEnabled } from './selectors';
import { uploadJob } from './uploadFile';
import { createUploadFolder } from './uploadFolder';

const MAX_PARALLEL = 3;
const THUMBNAIL_REGEN_DELAY_MS = 6000;

let globalAbortController: AbortController | null = null;

function ensureAbortController(): AbortSignal {
  if (!globalAbortController || globalAbortController.signal.aborted) {
    globalAbortController = new AbortController();
  }
  return globalAbortController.signal;
}

function refreshCatalogWithThumbnailRetry(dispatch: Dispatch) {
  // force: список этой локации закэширован (ключ фильтров не изменился), поэтому без
  // явного сброса ни только что загруженные файлы, ни их досчитанные превью не появились бы.
  dispatch(finderActions.loadAssetsRequested({ force: true }));
  setTimeout(() => {
    dispatch(finderActions.loadAssetsRequested({ force: true }));
  }, THUMBNAIL_REGEN_DELAY_MS);
}

export type UploadOptions = {
  // Upload into this folder of the selected project instead of the sidebar's category
  // (the destination picker of the upload modal / empty-catalog panel).
  categoryId?: string;
  // Upload into a new top-level folder of the selected project, created right before
  // the upload starts (MYUA-17010; see getUploadToProjectEnabled).
  newFolderName?: string;
};

export function useUploader() {
  const dispatch = useDispatch();
  const store = useStore<RootStore>();
  const enabled = useSelector(getEnabled);
  const uploadToProjectEnabled = useSelector(getUploadToProjectEnabled);
  const selectedId = useSelector(getSelectedId);
  const assetLabLogger = useSelector(getAssetLabLogger);

  const upload = useCallback(
    async (files: File[], options: UploadOptions = {}) => {
      const newFolderName = options.newFolderName?.trim() || null;
      const explicitTarget = Boolean(newFolderName || options.categoryId);
      if (files.length === 0 || (explicitTarget ? !uploadToProjectEnabled : !enabled)) {
        dispatch(actions.pendingUploadCleared());
        return;
      }
      let parentNodeId = Number.parseInt(options.categoryId ?? selectedId, 10);
      if (!newFolderName && (!Number.isFinite(parentNodeId) || parentNodeId <= 0)) {
        dispatch(actions.pendingUploadCleared());
        return;
      }

      const signal = ensureAbortController();

      // Fonts require a confirmed license before anything is uploaded. Detect them
      // (including fonts inside .zip archives) and block on the license form.
      // Gated per-instance by the backend `validate_font_license_before_uploading`
      // setting (My.Games only); absent -> false -> skip detection and the form.
      const validateFontLicense =
        getSettings(store.getState())?.validate_font_license_before_uploading ?? false;
      const fonts = validateFontLicense ? await detectFonts(files) : [];
      if (signal.aborted) {
        dispatch(actions.pendingUploadCleared());
        return;
      }
      if (fonts.length > 0) {
        const submission = await requestLicenseConfirmation(fonts, signal);
        // Cancel (or external abort while the form is open) -> abort the whole
        // upload (the license form is blocking).
        if (!submission) {
          dispatch(actions.pendingUploadCleared());
          return;
        }
        if (signal.aborted) {
          dispatch(actions.pendingUploadCleared());
          return;
        }
        // Bind the created license ids to their concrete File (an archive gets the
        // ids of every font found inside it). Lookup at upload time uses the same
        // File reference (via fileRegistry), so keys match 1:1.
        bindLicenseIdsToFiles(files, fonts, submission);
      }

      const validation = await api.fs.validateFilenames(files.map((f) => f.name));
      if (signal.aborted) {
        dispatch(actions.pendingUploadCleared());
        return;
      }

      if (!validation.success) {
        dispatch(actions.pendingUploadCleared());
        void message.error("Couldn't validate file names. Please try again.");
        return;
      }
      const validationErrors = validation.data.response.validation_result;

      // Created only after every check passed, so a cancelled license form or a failed
      // validation does not leave an empty folder behind.
      if (newFolderName) {
        const [projectId] = getSelectedProjectIds(store.getState());
        const folder = await createUploadFolder(
          dispatch as Dispatch,
          store.getState,
          projectId,
          newFolderName,
        );
        if (signal.aborted) {
          dispatch(actions.pendingUploadCleared());
          return;
        }
        if ('error' in folder) {
          dispatch(actions.pendingUploadCleared());
          void message.error(folder.error);
          return;
        }
        parentNodeId = folder.nodeId;
      }

      const newJobs: Job[] = files.map((file, idx) => {
        const j = makeJob(file, parentNodeId);
        const err = validationErrors[idx]?.error ?? null;
        if (err) {
          j.status = JobStatus.INVALID_FILE_NAME;
          j.errorMessage = err;
        } else {
          j.status = JobStatus.ENQUEUED;
          registerFile(j.uuid, file);
        }
        return j;
      });

      dispatch(actions.jobsInserted({ jobs: newJobs }));

      const queue = newJobs.filter((j) => j.status === JobStatus.ENQUEUED);
      if (queue.length === 0) return;

      queue.forEach(() => assetLabLogger({ action: 'Upload', sub_action: 'File' }));

      await runPool(queue, MAX_PARALLEL, (job) =>
        uploadJob(dispatch as Dispatch, job, signal, assetLabLogger),
      );

      if (!signal.aborted) {
        refreshCatalogWithThumbnailRetry(dispatch as Dispatch);
      }
    },
    [enabled, uploadToProjectEnabled, selectedId, dispatch, assetLabLogger, store],
  );

  const cancelAll = useCallback(() => {
    globalAbortController?.abort();
    globalAbortController = null;
    clearRegistry();
    dispatch(actions.cancelAllJobs());
  }, [dispatch]);

  const retryJob = useCallback(
    async (jobID: string) => {
      const job = store.getState().uploader.jobs.find((j) => j.uuid === jobID);
      if (!job) return;
      if (job.status !== JobStatus.UPLOAD_FAILURE && job.status !== JobStatus.BIND_FAILURE) return;

      const signal = ensureAbortController();
      dispatch(actions.jobRestarted({ jobID }));

      await uploadJob(dispatch as Dispatch, job, signal, assetLabLogger);

      if (!signal.aborted) {
        refreshCatalogWithThumbnailRetry(dispatch as Dispatch);
      }
    },
    [dispatch, store, assetLabLogger],
  );

  return { upload, cancelAll, retryJob, enabled };
}

async function runPool<T>(items: T[], concurrency: number, worker: (item: T) => Promise<void>) {
  let next = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const idx = next++;
      await worker(items[idx]);
    }
  });
  await Promise.all(workers);
}
